import { useCallback, useMemo } from 'react';

import {
  backerKeys,
  useDeleteConsumerFamilyBacker,
  useShowConsumerFamily,
} from '@givebacks/api/backer';
import { useUserData } from '@givebacks/api/core';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Modal, Stack, Text, notifications } from 'ui';

import { getErrorMessage_v2 } from '../../../../../utils/errorMessage';
import {
  ModalTitleWithBackButton,
  useDirectorySettingsModalProvider,
} from '../DirectorySettingsModalProvider';

const RemoveAdultModalFooter = () => {
  const { meta, openModalById } = useDirectorySettingsModalProvider({});

  const { data: family } = useShowConsumerFamily({
    familyId: meta.familyId,
    include: 'backers',
  });

  const { data: backer } = useShowConsumerFamily({
    familyId: meta.familyId,
    include: 'backers',
    select: (data) => data.backers?.find((backer) => backer.uuid === meta.backerId),
  });

  const { data: user } = useUserData();

  const { mutate: deleteConsumerFamilyBacker } = useDeleteConsumerFamilyBacker();
  const queryClient = useQueryClient();

  const isRemoveSelf = useMemo(() => {
    return meta.backerId === user?.uuid;
  }, [meta.backerId, user?.uuid]);

  const isLastAdult = useMemo(() => {
    return family?.backers?.length === 1;
  }, [family?.backers?.length]);

  const handleRemove = useCallback(() => {
    if (!meta.familyId || !meta.backerId) return;

    const backerName = isRemoveSelf
      ? 'yourself'
      : `${backer?.first_name || 'an adult'} ${backer?.last_name || ''}`;

    const message =
      isRemoveSelf && isLastAdult
        ? `You've removed yourself and unlisted your family`
        : `You've removed ${backerName} from ${family?.name} family`;

    deleteConsumerFamilyBacker(
      { familyId: meta.familyId, backerId: meta.backerId },
      {
        onSuccess: () => {
          notifications.show({
            message: message,
            variant: 'success',
          });
          void queryClient.invalidateQueries({ queryKey: backerKeys.consumer.families.all });

          if (isRemoveSelf) {
            openModalById('families');
          } else {
            openModalById('members');
          }
        },
        onError: (error) => {
          notifications.show({
            message: getErrorMessage_v2(error),
            variant: 'error',
          });
        },
      }
    );
  }, [
    meta.familyId,
    meta.backerId,
    isRemoveSelf,
    backer?.first_name,
    backer?.last_name,
    isLastAdult,
    family?.name,
    deleteConsumerFamilyBacker,
    queryClient,
    openModalById,
  ]);

  return (
    <Modal.AffixedFooter>
      <Button color="cerise" fullWidth onClick={handleRemove}>
        Confirm Removal
      </Button>
    </Modal.AffixedFooter>
  );
};

const RemoveAdultModalContent = () => {
  const { meta } = useDirectorySettingsModalProvider({});

  const { data: family } = useShowConsumerFamily({
    familyId: meta.familyId,
    include: 'backers',
  });
  const { data: backer } = useShowConsumerFamily({
    familyId: meta.familyId,
    include: 'backers',
    select: (data) => data.backers?.find((backer) => backer.uuid === meta.backerId),
  });

  const { data: user } = useUserData();

  const isLastAdult = useMemo(() => {
    return meta.backerId === user?.uuid && family?.backers?.length === 1;
  }, [family?.backers?.length, meta.backerId, user?.uuid]);

  const backerName = useMemo(() => {
    return meta.backerId === user?.uuid ? 'yourself' : `${backer?.first_name} ${backer?.last_name}`;
  }, [backer?.first_name, backer?.last_name, meta.backerId, user?.uuid]);

  return (
    <Stack gap="lg">
      {isLastAdult && (
        <Alert type="warning">
          You’re the only adult listed for your family. If you remove yourself, your family will no
          longer be listed in the directory.
        </Alert>
      )}
      <Text fw={600}>
        {isLastAdult
          ? `Are you sure you want to remove yourself and unlist your family?`
          : `Are you sure you want to remove ${backerName}?`}
      </Text>
    </Stack>
  );
};

const RemoveAdultModal = () => {
  const { opened, close } = useDirectorySettingsModalProvider({
    id: 'remove-adult',
  });
  return (
    <Modal
      opened={opened}
      onClose={close}
      title={<ModalTitleWithBackButton>Remove Adult</ModalTitleWithBackButton>}
    >
      <RemoveAdultModalContent />
      <RemoveAdultModalFooter />
    </Modal>
  );
};

export { RemoveAdultModal };
